/**
 * Comptage de segments SMS — GSM-7 / UCS-2.
 *
 * ⚠️ TRANSCRIPTION de `Provisioning_v2/src/services/SmsRender.js`. Les deux DOIVENT rendre le
 * même verdict sur le même texte : si cet écran annonce un segment et que la passerelle en
 * facture deux, personne ne pourra expliquer la facture. Toute correction ici doit être reportée
 * là-bas, et le banc `smsSegments.test.js` rejoue les cas du banc serveur pour le détecter.
 *
 * Pourquoi une copie plutôt qu'un module partagé : les deux dépôts sont distincts et n'ont pas de
 * paquet commun. Le jour où il en existe un, cette fonction est la première à y déménager.
 *
 * Pourquoi la mesure est nécessaire À LA SAISIE : un SMS est facturé au segment, et l'encodage
 * décide de la capacité — 160 caractères en GSM-7, 70 dès qu'un seul caractère en sort. Le piège
 * français est que le `ç` MINUSCULE en sort (le `Ç` majuscule, lui, y est), tout comme ê â î ô û,
 * œ, « », et l'apostrophe typographique ’ que Word et iOS substituent silencieusement. Un « ça »
 * fait donc passer un message de 1 à 2 segments sans que rien à l'écran ne change.
 */

/** Alphabet GSM 03.38 par défaut (0x00–0x7F), hors ESC. */
const GSM7_BASIC = new Set([
  '@', '£', '$', '¥', 'è', 'é', 'ù', 'ì', 'ò', 'Ç', '\n', 'Ø', 'ø', '\r', 'Å', 'å',
  'Δ', '_', 'Φ', 'Γ', 'Λ', 'Ω', 'Π', 'Ψ', 'Σ', 'Θ', 'Ξ', 'Æ', 'æ', 'ß', 'É',
  ' ', '!', '"', '#', '¤', '%', '&', "'", '(', ')', '*', '+', ',', '-', '.', '/',
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ':', ';', '<', '=', '>', '?',
  '¡', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O',
  'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'Ä', 'Ö', 'Ñ', 'Ü', '§',
  '¿', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o',
  'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'ä', 'ö', 'ñ', 'ü', 'à',
]);

/** Représentables en GSM-7, mais sur DEUX septets (ESC + caractère). */
const GSM7_EXTENDED = new Set(['\f', '^', '{', '}', '\\', '[', '~', ']', '|', '€']);

const GSM7_SINGLE = 160;
const GSM7_MULTI = 153;
const UCS2_SINGLE = 70;
const UCS2_MULTI = 67;

export function isGsm7(text) {
  for (const ch of String(text ?? '')) {
    if (!GSM7_BASIC.has(ch) && !GSM7_EXTENDED.has(ch)) return false;
  }
  return true;
}

function unitCost(text, gsm7) {
  if (!gsm7) return String(text ?? '').length;   // .length EST le compte d'unités UTF-16
  let cost = 0;
  for (const ch of String(text ?? '')) cost += GSM7_EXTENDED.has(ch) ? 2 : 1;
  return cost;
}

/**
 * Découpe réelle. Pas une division : un caractère ne se coupe pas en deux. Un « € » pèse deux
 * septets qui doivent rester dans le même segment, et un emoji deux unités UTF-16. Diviser le
 * coût total par la capacité sous-estimerait le nombre de segments — donc la facture.
 */
function segmentCount(text, gsm7, capacity) {
  const chars = Array.from(String(text ?? ''));
  let segments = 1, used = 0;
  for (const ch of chars) {
    const cost = gsm7 ? (GSM7_EXTENDED.has(ch) ? 2 : 1) : ch.length;
    if (used + cost > capacity) { segments++; used = cost; }
    else { used += cost; }
  }
  return segments;
}

/**
 * @returns {{encoding: 'GSM-7'|'UCS-2', length: number, segments: number,
 *            capacity: number, remaining: number, forcedUcs2By: string[]}}
 * `forcedUcs2By` donne la RAISON et pas seulement le verdict : annoncer « UCS-2 » ne dit pas
 * quoi corriger, montrer le « ç » le dit.
 */
export function measureSms(text) {
  const s = String(text ?? '');
  const gsm7 = isGsm7(s);
  const cost = unitCost(s, gsm7);
  const single = gsm7 ? GSM7_SINGLE : UCS2_SINGLE;
  const multi = gsm7 ? GSM7_MULTI : UCS2_MULTI;

  // Un message d'un seul segment n'a pas d'en-tête de concaténation : il a la capacité PLEINE.
  const segments = cost <= single ? 1 : segmentCount(s, gsm7, multi);
  const capacity = segments === 1 ? single : multi * segments;

  const offenders = [];
  if (!gsm7) {
    for (const ch of s) {
      if (!GSM7_BASIC.has(ch) && !GSM7_EXTENDED.has(ch) && offenders.indexOf(ch) < 0) {
        offenders.push(ch);
      }
    }
  }
  return {
    encoding: gsm7 ? 'GSM-7' : 'UCS-2',
    length: cost,
    segments,
    capacity,
    remaining: capacity - cost,
    forcedUcs2By: offenders,
  };
}

const smsSegments = { measureSms, isGsm7 };
export default smsSegments;
